// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeroLayout1Data } from '../layout_1/hero-layout-1.component';

/**
 * Lightweight preview component for the "Hero Layout 4" template used in the website builder; renders hero content (title, subtitle, image and action) with configurable typography and colors and emits an event when the primary button is clicked.
 * @component
 * @selector app-hero-layout-4-preview
 * @example
 *   <app-hero-layout-4-preview [data]="sampleData" (buttonClick)="handleHeroButton($event)"></app-hero-layout-4-preview>
 * @input {HeroLayout1Data | null} data - The model containing title, subtitle, image metadata, and action/link information used to populate the hero.
 * @input {string | null} headingColor - Optional CSS color value applied to the heading text.
 * @input {string | null} paragraphColor - Optional CSS color value applied to body/paragraph text.
 * @input {string | null} headingFont - Optional font-family applied to the heading.
 * @input {string | null} bodyFont - Optional font-family applied to body/paragraph text.
 * @input {string | null} primaryColor - Optional primary theme color used for actionable elements (e.g., buttons).
 * @input {any} titleStyles - Optional inline style overrides for the title element (object of CSS properties).
 * @output {{ link: string; event: Event }} buttonClick - Emitted when the hero's primary action button is activated; payload contains the destination link (if any) and the originating DOM event.
 * @method onButtonClick(link: string | undefined, event: Event): void - Handler invoked when the hero action is clicked. Validates presence of a link and emits the buttonClick event with the link and original event.
 * @method getImageSrc(): string - Resolves and returns the image source prioritizing data.image.src, falling back to data.imageUrl, or empty string if none.
 * @method getImageAlt(): string - Returns the image alt text from the data model or a sensible default ("Car Wash") when not provided.
 * @method getBackgroundImage(): string - Produces a CSS background-image value (e.g., "url(...)") when an image source is available, otherwise returns an empty string.
 * @lifecyclehooks This component does not implement Angular lifecycle hooks directly and relies on input-bound rendering; any initialization or change handling should be managed by parent inputs.
 */
@Component({
  selector: 'app-hero-layout-4-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-4.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-4.component.scss'],
})
export class HeroLayout4PreviewComponent {
  @Input() data: HeroLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  getBackgroundImage(): string {
    const imageSrc = this.getImageSrc();
    return imageSrc ? `url(${imageSrc})` : '';
  }
}
